import { useState, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Label, Card } from '../components/ui';

export function SignInPage() {
  const { signIn, signInWithGoogle } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function postSigninRedirect() {
    const returnTo = params.get('returnTo');
    nav(returnTo && returnTo.startsWith('/') ? returnTo : '/');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await signIn(email, password);
      postSigninRedirect();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setErr(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      postSigninRedirect();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="font-serif text-2xl font-semibold mb-2">Sign in</h1>
        <p className="text-sm text-soft mb-6">Welcome back.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <div className="my-4 text-center text-xs text-soft">or</div>
        <Button variant="secondary" onClick={handleGoogle} disabled={loading} className="w-full">
          Continue with Google
        </Button>
        <p className="mt-6 text-sm text-soft text-center">
          New user? <Link to="/signup" className="text-forest-700 hover:underline">Sign up</Link>
        </p>
        <p className="mt-3 text-xs text-soft text-center">
          Trouble signing in or with billing? Email{' '}
          <a href="mailto:homescholarpro@gmail.com" className="text-forest-700 hover:underline">homescholarpro@gmail.com</a>
        </p>
      </Card>
    </div>
  );
}

export function SignUpPage() {
  const { signUp, signInWithGoogle, user, refreshMembership } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // An invite link (/signup?code=ABC123) carries a member code through to the
  // first-run setup, which opens on the "Join" step with it prefilled.
  const inviteCode = (params.get('code') || '').toUpperCase();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // After creating the account, go to first-run setup. There the user chooses
  // to create a new household (no code) or join an existing one (code). No
  // code is required to sign up or to create a household.
  function goToSetup() {
    nav('/welcome' + (inviteCode ? `?code=${encodeURIComponent(inviteCode)}` : ''));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await signUp(email, password);
      await refreshMembership();
      goToSetup();
    } catch (e: unknown) {
      console.error('[Auth] Email signup failed:', e);
      setErr(e instanceof Error ? e.message : 'Sign up failed');
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setErr(null);
    setLoading(true);
    try {
      await signInWithGoogle();
      await refreshMembership();
      goToSetup();
    } catch (e: unknown) {
      console.error('[Auth] Google signup failed:', e);
      setErr(e instanceof Error ? e.message : 'Sign up failed');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream px-4">
      <Card className="w-full max-w-md p-8">
        <h1 className="font-serif text-2xl font-semibold mb-2">Create your account</h1>
        <p className="text-sm text-soft mb-6">
          {inviteCode
            ? "You're joining an existing household — we'll confirm your invite code next. Invited members don't pay; your household's subscription covers everyone."
            : "Create your account, then set up your household next. HomeScholar Pro is $14.99/year per household (up to 8 members)."}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">Password (8+ Chars)</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
          </div>
          {err && <p className="text-sm text-rose-600">{err}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </Button>
        </form>
        <div className="my-4 text-center text-xs text-soft">or</div>
        <Button variant="secondary" onClick={handleGoogle} disabled={loading} className="w-full">
          Continue with Google
        </Button>
        <p className="mt-6 text-sm text-soft text-center">
          Already have an account? <Link to="/signin" className="text-forest-700 hover:underline">Sign in</Link>
        </p>
        {user && (
          <p className="mt-3 text-xs text-soft text-center">Already signed in as {user.email}</p>
        )}
        <p className="mt-3 text-xs text-soft text-center">
          Trouble signing up or with billing? Email{' '}
          <a href="mailto:homescholarpro@gmail.com" className="text-forest-700 hover:underline">homescholarpro@gmail.com</a>
        </p>
      </Card>
    </div>
  );
}
